import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEventContext } from '../../../context/EventContext';
import { useStreamContext } from '../../../context/StreamContext';
import { useModalContext } from '../../../context/ModalContext';
import { useLocaleContext } from '../../../context/LocaleContext';
import { useAvaliableWidgetRecord } from '../../../hooks/useAvaliableWidgetRecord';
import { unixDiff, unixFormat } from '../../../helpers/times';
import MembersScheduleModalView from '../../Members/MembersViews/MembersScheduleModalView';
import MemberDetailsModal from '../../Members/MembersComponents/MemberDetailsModal';
import ScheduleEpisodeStatus from '../ScheduleComponents/ScheduleEpisodeStatus';
import ScheduleUserCategory from '../ScheduleComponents/ScheduleUserCategory';
import ScheduleFullness from '../ScheduleComponents/ScheduleFullness';
import PartnersBaseView from '../../Partners/PartnersViews/PartnersBaseView';
import VideoEmbedModal from '../../VideoEmbedModal';
import { CAttachedFile, CButton, CTags } from '../../UI';
import { usePlayerContext } from '../../../context/PlayerContext';
import QuestionWriteForm from '../../../controllers/Questions/QuestionWriteForm';
import EpisodeRecordStatus from '../ScheduleComponents/EpisodeRecordStatus';
import { useWidgetContext } from '../../../context/WidgetContextProvider';
import { TYPE_BROADCAST } from '../../../constants/widget-types';
import storage from '../../../../../player/js/storage';
import UserAuthenticationModal from '../../User/UserAuthenticationModal';
import { useUserContext } from '../../../context/UserContext';
import { episodeLikeToggle } from '../../../utils/api';
import { getEpisodeStatus, mappedEpisodes } from '../../../helpers/schedule-helper';
import { EPISODE_STATUSES, RECORD_STATUSES, TYPE_OFFLINE } from '../../../constants/common';
import { useApplicationContext } from '../../../context/ApplicationContext';
import Icons from '../../Icons';

import './style.scss';

const ScheduleDetailsModal = ({ episode, settings }) => {
    const { event, eventKey, watchKey } = useEventContext();
    const { widgetSettings, widgetType, isWidget } = useWidgetContext();
    const { handleBookmark, bookmarkEpisodeIds } = useApplicationContext();
    const { isPlayerWidget } = usePlayerContext();
    const { user: currentUser } = useUserContext();
    const { renderModal } = useModalContext();
    const $locale = useLocaleContext();
    const streamContext = useStreamContext();
    const [isMultiDaysEvent, setMultiDaysEvent] = React.useState(false);
    const hasEpisodeRecord = useAvaliableWidgetRecord(episode);
    const showWidgetWatchEpisodeButton = settings?.showEpisodePopupWatchEpisode;
    const showWidgetCalendar = widgetType === TYPE_BROADCAST ? !widgetSettings?.scheduleModalAddCalendarButtonHidden : settings?.showEpisodePopupAddToCalendar;
    const ratedStorageKey = `${eventKey}/episodes/rated`;
    const ratedEpisodes = storage.get(ratedStorageKey) || {};
    const [isRated, setIsRated] = useState(ratedEpisodes[episode.id]?.includes(currentUser?.id));
    const navigate = useNavigate();
    const episodeWatchPage = currentUser?.participationType === TYPE_OFFLINE
        ? 'halls'
        : 'broadcast';

    const isOpenInPlayer = useMemo(() => {
        const episodeStatus = getEpisodeStatus(episode);

        return settings?.showWatchRecordButton
            && (streamContext.recordStatus === RECORD_STATUSES.EPISODES_CUT
                || episodeStatus === EPISODE_STATUSES.record
                || episodeStatus === EPISODE_STATUSES.live);
    }, [episode]);

    const numberLikes = useMemo(() => mappedEpisodes(event.stream)?.find(({ id }) => id === episode.id)?.like, [event, episode?.id]);

    const checkAuth = (currentUser) => {
        return new Promise((resolve) => {
            if (!currentUser) {
                renderModal(UserAuthenticationModal, {
                    event,
                    $locale,
                    onClose: (props) => resolve(props?.isAuth),
                }, null, true);
            } else {
                resolve(true);
            }
        });
    };

    const handleRateToggle = async (e) => {
        e.stopPropagation();

        const isAuth = await checkAuth(currentUser);
        if (isAuth) {
            setIsRated(isRated => !isRated);
            const value = !isRated;
            const ratedEpisodes = storage.get(ratedStorageKey) || {};
            const arr = ratedEpisodes[episode.id] || [];
            const ratedStorageValue = { ...ratedEpisodes, [episode.id]: value ? [...arr, currentUser?.id] : arr.filter(id => id !== currentUser?.id) };
            storage.set(ratedStorageKey, ratedStorageValue, storage.DAY * 30);
            await episodeLikeToggle(episode.id, value);
        }
    }

    function handleOpenVideo(videoData) {
        renderModal(VideoEmbedModal, { embedLink: videoData.link });
    }

    useEffect(() => {
        setMultiDaysEvent(!!unixDiff(event.timeStart, event.timeStop, 'days'));
    }, []);

    const handleOpenQuestionWriteForm = (member) => {
        renderModal(
            QuestionWriteForm,
            { episode, member, selectStream: episode.stream, $locale },
            null,
            true,
        );
    };

    const isShowButtonQuestion = (isWidget ? settings?.showAskQuestionButton : event.moduleQuestion.enabled) && event.moduleEpisode.membersEnabled && event.moduleQuestion.allowTargetMessage;
    const isBookmarkVisible = !isWidget && event?.moduleEpisode?.episodeFavorite;
    const isBookmark = bookmarkEpisodeIds.includes(episode.id);

    return (
        <div className="schedule-details-modal">
            <h2 className="schedule-details-modal-title">
                {$locale.getProperty(episode, 'name')}
            </h2>
            {isOpenInPlayer && (
                <CButton
                    icon="play"
                    theme="accent"
                    iconStyles="play"
                    className="schedule-details-modal-watch"
                    label={$locale.get('label.episode.watch')}
                    onClick={() => navigate(`/event/${watchKey}/${episodeWatchPage}?stream=${episode.streamId}&episode=${episode.id}`)}
                />
            )}
            <div className="schedule-details-modal-header">
                {
                    isMultiDaysEvent &&
                    <p className="schedule-details-modal-date">
                        {unixFormat(episode.timeStart, 'DD MMMM')}
                    </p>
                }
                <div className="schedule-details-modal-row">
                    <div className="schedule-details-modal-row">
                        <p className="schedule-details-modal-time">
                            {unixFormat(episode.timeStart, 'HH:mm')} — {unixFormat(episode.timeStop, 'HH:mm')}
                        </p>
                        {
                            (streamContext.streams?.length > 1) &&
                            <div className="schedule-details-modal-hall">
                                {
                                    !!episode.stream?.color &&
                                    <span
                                        className="schedule-details-modal-hall-indicator"
                                        style={{
                                            backgroundColor: episode?.stream?.color ?? '',
                                        }}
                                    />
                                }
                                {$locale.getProperty(episode.stream, 'description')}
                            </div>
                        }
                    </div>
                    <div className="schedule-details-modal-row">
                        <EpisodeRecordStatus
                            episode={episode}
                            displayRecord={settings?.displayEpisodeRecord ?? false}
                        >
                            <>
                                {
                                    (settings?.showEpisodePopupStatus ?? settings?.showStatuses) &&
                                    <ScheduleEpisodeStatus
                                        timeStart={episode.timeStart}
                                        timeStop={episode.timeStop}
                                    />
                                }
                                {/* Скрыто т.к. пока не используется */}
                                {/* <ScheduleUserCategory /> */}
                                {
                                    ((settings?.showEpisodePopupFormat ?? settings?.showFormats) && !isPlayerWidget) &&
                                    <CTags
                                        single
                                        tag={$locale.get(`participationType.${episode.displayParticipationType}`)}
                                    />
                                }
                            </>
                        </EpisodeRecordStatus>
                    </div>
                </div>
            </div>
            <div className="schedule-details-modal-row">
                <div className="schedule-details-modal-row">
                    {/* Скрыто т.к. пока не используется */}
                    {/* <CButton
                        theme="accent"
                        label={$locale.get('label.session.submit')}
                    />*/}
                    {showWidgetWatchEpisodeButton &&
                        <CButton
                            icon="play"
                            theme="accent"
                            iconStyles="play"
                            className="schedule-details-modal-watch-episode"
                            label={$locale.get('label.episode.watchEpisode')}
                            onClick={() => window.open(`${location.origin}/event/${event.eventKey}/${episodeWatchPage}?stream=${episode.streamId}&episode=${episode.id}`, '_blank')}
                        />
                    }
                    {
                        (showWidgetCalendar ?? settings?.showAddToCalendar) &&
                        <CButton
                            label={$locale.get('label.calendar.add')}
                            icon="calendar"
                            iconStyles="calendar"
                            onClick={() => window.open(`/api/player/episode/${episode.id}/calendar.ics?locale=${$locale.name}`, '_blank')}
                        />
                    }
                    {isBookmarkVisible && (
                        <CButton
                            label={$locale.get('label.favorite.add')}
                            icon="bookmark"
                            theme={isBookmark ? "accent" : "default"}
                            onClick={() => handleBookmark(episode.id)}
                        />
                    )}
                </div>
                {/* Скрыто т.к. пока не используется */}
                {/* <ScheduleFullness /> */}
            </div>
            <CTags tags={episode.tags} />
            <div className="schedule-details-modal-common">
                <div className="schedule-details-modal-annotation">
                    <PartnersBaseView partners={episode.partners} />
                    {
                        (hasEpisodeRecord && episode.displayRecord && !!settings?.displayEpisodeRecord) &&
                        <div className="schedule-details-modal-record">
                            <iframe
                                src={`/player/${episode.key}`}
                                frameBorder="0"
                                scrolling="no"
                                style={{ width: '100%', aspectRatio: '16/9' }}
                                allowFullScreen
                            />
                        </div>
                    }
                    <p className="schedule-details-modal-info">
                        {$locale.getProperty(episode, 'info')}
                    </p>
                </div>
                {
                    !!episode.videos.length &&
                    <CButton
                        icon="play"
                        label={$locale.get('label.video.watch')}
                        onClick={() => handleOpenVideo(episode.videos[0])}
                    />
                }
            </div>
            <MembersScheduleModalView
                episode={episode}
                photoVisible={settings?.showPhotos ?? true}
                onMemberClick={(member) => {
                    renderModal(
                        MemberDetailsModal,
                        { lockPageScroll: false, member, settings },
                        null,
                        (settings?.showMemberPopup ?? true),
                    )
                }}
                onMessageClick={isShowButtonQuestion ? handleOpenQuestionWriteForm : null}
            />
            {
                !!episode.files?.length &&
                <div className="schedule-details-modal-materials">
                    <div className="schedule-details-modal-materials__title">
                        {$locale.get('title.materials')}
                    </div>
                    <div className="schedule-details-modal-materials__list">
                        {
                            episode.files.map((file) => (
                                <CAttachedFile
                                    key={file.id}
                                    file={file}
                                />
                            ))
                        }
                    </div>
                </div>
            }
            <div className="schedule-details-modal-footer">
                {!isWidget && event.moduleEpisode?.like && (
                    <a onClick={handleRateToggle} className={`schedule-details-modal-like ${isRated ? '-active' : ''}`}>
                        <Icons name="like_message" active={isRated} />{numberLikes}
                    </a>
                )}
            </div>
        </div>
    );
};

export default ScheduleDetailsModal;
